import { uiText } from '../utils/i18n';
import { useState, useMemo } from 'react';
import { useT } from '../utils/i18n';
import { PRESET_CATEGORIES, type PresetAgent } from '../data/preset-agents';
import styles from './CreateAgentModal.module.css';

interface CreateAgentModalProps {
  onClose: () => void;
  onCreated: (name: string) => void;
}

type Mode = 'choose' | 'manual' | 'preset';
const CODEX_DEFAULTS = {
  engine: 'codex' as const,
  codex: {
    model: 'gpt-5.6-sol',
    reasoningEffort: 'high',
    approvalPolicy: 'never',
    sandbox: 'workspace-write',
  },
};

// Cache for fetched souls
const soulCache = new Map<string, string>();

async function fetchSoul(key: string): Promise<string> {
  if (soulCache.has(key)) return soulCache.get(key)!;
  try {
    const res = await fetch('/web/preset-souls.json');
    if (res.ok) {
      const data = await res.json();
      for (const [k, v] of Object.entries(data)) {
        soulCache.set(k, v as string);
      }
      return soulCache.get(key) || '';
    }
  } catch { /* ignore */ }
  return '';
}

export function CreateAgentModal({ onClose, onCreated }: CreateAgentModalProps) {
  const [mode, setMode] = useState<Mode>('choose');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [soul, setSoul] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  // Preset state
  const [activeCategory, setActiveCategory] = useState(PRESET_CATEGORIES[0]?.id || '');
  const [selectedPreset, setSelectedPreset] = useState<PresetAgent | null>(null);
  const [presetSearch, setPresetSearch] = useState('');

  const t = useT();
  const token = localStorage.getItem('novax:token') || '';

  const filteredAgents = useMemo(() => {
    const cat = PRESET_CATEGORIES.find(c => c.id === activeCategory);
    if (!cat) return [];
    if (!presetSearch.trim()) return cat.agents;
    const q = presetSearch.toLowerCase();
    return cat.agents.filter(a =>
      a.name.toLowerCase().includes(q) ||
      a.nameZh.toLowerCase().includes(q) ||
      a.desc.toLowerCase().includes(q)
    );
  }, [activeCategory, presetSearch]);

  // Global search across all categories
  const globalResults = useMemo(() => {
    if (!presetSearch.trim()) return null;
    const q = presetSearch.toLowerCase();
    const results: PresetAgent[] = [];
    for (const cat of PRESET_CATEGORIES) {
      for (const a of cat.agents) {
        if (a.name.toLowerCase().includes(q) || a.nameZh.toLowerCase().includes(q) || a.desc.toLowerCase().includes(q)) {
          results.push(a);
        }
      }
    }
    return results.length > 0 ? results : null;
  }, [presetSearch]);

  const handleCreate = async (agentName: string, agentDesc: string, agentSoul: string) => {
    const trimmed = agentName.trim();
    if (!trimmed) { setError(t('create.nameRequired')); return; }

    setCreating(true);
    setError('');
    try {
      const res = await fetch('/api/bots', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          platform: 'web',
          name: trimmed,
          description: agentDesc.trim() || undefined,
          ...CODEX_DEFAULTS,
          defaultWorkingDirectory: '/tmp',
          installSkills: true,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || `Failed (${res.status})`);
        return;
      }
      const createdName = typeof data.name === 'string' && data.name ? data.name : trimmed;

      if (agentSoul.trim()) {
        await fetch(`/api/agent-soul/${encodeURIComponent(createdName)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ soul: agentSoul.trim() }),
        });
      }

      onCreated(createdName);
    } catch (err: any) {
      setError(err.message || 'Network error');
    } finally {
      setCreating(false);
    }
  };

  const handlePresetCreate = async () => {
    if (!selectedPreset) return;
    const soulContent = await fetchSoul(selectedPreset.key);
    await handleCreate(selectedPreset.nameZh, selectedPreset.descZh || selectedPreset.desc, soulContent);
  };

  // Choose mode
  if (mode === 'choose') {
    return (
      <div className={styles.overlay} onClick={onClose}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
          <h2 className={styles.title}>{t('create.title')}</h2>
          <p className={styles.subtitle}>Choose how to create your agent</p>
          <div className={styles.modeCards}>
            <button className={styles.modeCard} onClick={() => setMode('preset')}>
              <span className={styles.modeIcon}>📋</span>
              <span className={styles.modeLabel}>{uiText("从预设创建")}</span>
              <span className={styles.modeDesc}>{uiText("从 160+ 专业角色模板中选择")}</span>
            </button>
            <button className={styles.modeCard} onClick={() => setMode('manual')}>
              <span className={styles.modeIcon}>✏️</span>
              <span className={styles.modeLabel}>{uiText("手动创建")}</span>
              <span className={styles.modeDesc}>{uiText("自定义名称、描述和人格")}</span>
            </button>
          </div>
          <div className={styles.actions}>
            <button className={styles.cancelBtn} onClick={onClose}>{t('create.cancel')}</button>
          </div>
        </div>
      </div>
    );
  }

  // Manual mode
  if (mode === 'manual') {
    return (
      <div className={styles.overlay} onClick={onClose}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <button className={styles.backLink} onClick={() => setMode('choose')}>{uiText("← 返回")}</button>
            <h2 className={styles.title}>{uiText("手动创建")}</h2>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>{t('create.name')}</label>
            <input className={styles.input} value={name} onChange={(e) => setName(e.target.value)} placeholder={t('create.namePlaceholder')} autoFocus />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>{t('create.description')}</label>
            <input className={styles.input} value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t('create.descPlaceholder')} />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>{uiText("运行引擎")}</label>
            <span className={styles.engineHint}>Codex · gpt-5.6-sol · high</span>
          </div>
          <div className={styles.field}>
            <label className={styles.label}>{t('create.soul')}</label>
            <textarea className={styles.textarea} value={soul} onChange={(e) => setSoul(e.target.value)} placeholder={t('create.soulPlaceholder')} rows={8} spellCheck={false} />
          </div>

          {error && <p className={styles.error}>{uiText(error)}</p>}
          <div className={styles.actions}>
            <button className={styles.cancelBtn} onClick={onClose}>{t('create.cancel')}</button>
            <button className={styles.createBtn} onClick={() => handleCreate(name, description, soul)} disabled={creating}>
              {creating ? t('create.creating') : t('create.create')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Preset mode
  const displayAgents = globalResults && presetSearch.trim() ? globalResults : filteredAgents;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={`${styles.modal} ${styles.modalWide}`} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <button className={styles.backLink} onClick={() => setMode('choose')}>{uiText("← 返回")}</button>
          <h2 className={styles.title}>{uiText("从预设创建")}</h2>
        </div>

        {/* Search */}
        <input
          className={styles.input}
          type="text"
          placeholder={uiText("搜索预设角色...")}
          value={presetSearch}
          onChange={(e) => setPresetSearch(e.target.value)}
        />

        <div className={styles.field}>
          <label className={styles.label}>{uiText("运行引擎")}</label>
          <span className={styles.engineHint}>Codex · gpt-5.6-sol · high</span>
        </div>

        <div className={styles.presetLayout}>
          {/* Left: category tabs */}
          <div className={styles.categoryTabs}>
            {PRESET_CATEGORIES.map(cat => (
              <button
                key={cat.id}
                className={`${styles.categoryTab} ${activeCategory === cat.id ? styles.categoryTabActive : ''}`}
                onClick={() => { setActiveCategory(cat.id); setSelectedPreset(null); }}
              >
                <span className={styles.catLabel}>{cat.label}</span>
                <span className={styles.catCount}>{cat.agents.length}</span>
              </button>
            ))}
          </div>

          {/* Right: agent list */}
          <div className={styles.presetList}>
            {displayAgents.map(agent => (
              <button
                key={agent.key}
                className={`${styles.presetItem} ${selectedPreset?.key === agent.key ? styles.presetItemActive : ''}`}
                onClick={() => setSelectedPreset(agent)}
              >
                <span className={styles.presetEmoji}>{agent.emoji}</span>
                <div className={styles.presetInfo}>
                  <span className={styles.presetName}>{agent.nameZh}</span>
                  <span className={styles.presetDesc}>{(agent.descZh || agent.desc).length > 80 ? (agent.descZh || agent.desc).slice(0, 80) + '...' : (agent.descZh || agent.desc)}</span>
                </div>
              </button>
            ))}
            {displayAgents.length === 0 && (
              <div className={styles.presetEmpty}>{uiText("没有匹配的角色")}</div>
            )}
          </div>
        </div>

        {error && <p className={styles.error}>{uiText(error)}</p>}
        <div className={styles.actions}>
          <button className={styles.cancelBtn} onClick={onClose}>{t('create.cancel')}</button>
          <button className={styles.createBtn} onClick={handlePresetCreate} disabled={creating || !selectedPreset}>
            {creating ? t('create.creating') : selectedPreset ? `创建 ${selectedPreset.nameZh}` : uiText("请选择一个角色")}
          </button>
        </div>
      </div>
    </div>
  );
}
